import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { authApi, getToken, setToken } from "../lib/api";

const AuthContext = createContext(null);
const USER_KEY = "kt_user";
const readCachedUser = () => { try { return getToken() ? JSON.parse(localStorage.getItem(USER_KEY) || "null") : null; } catch { return null; } };
const cacheUser = (u) => (u ? localStorage.setItem(USER_KEY, JSON.stringify(u)) : localStorage.removeItem(USER_KEY));

export const ROLE_HOME = {
  rider: "/app/home",
  customer: "/app/home",
  driver: "/app/driver",
  restaurant: "/app/restaurant/orders",
  admin: "/app/admin/verifications",
  manager: "/app/admin/verify/restaurants",
  agent: "/app/admin/tasks",
};

export function homeForRole(role) {
  return ROLE_HOME[role] || "/app/home";
}

export function AuthProvider({ children }) {
  const [user, setUserState] = useState(readCachedUser);
  const [loading, setLoading] = useState(() => !readCachedUser());
  const setUser = useCallback((u) => { cacheUser(u); setUserState(u); }, []);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return null;
    }
    try {
      const me = await authApi.me();
      setUser(me.user || me);
      return me.user || me;
    } catch (e) {
      if (e?.response?.status === 401) {
        setToken(null);
        setUser(null);
      }
      // Network error (offline): keep the cached user so the app shell still renders.
      return e?.response ? null : readCachedUser();
    } finally {
      setLoading(false);
    }
  }, [setUser]);

  useEffect(() => {
    // Returning from Google OAuth: AuthCallback exchanges the session_id first, skip /me here.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const applyAuth = useCallback((data) => {
    if (data?.token) setToken(data.token);
    if (data?.user) setUser(data.user);
    return data?.user;
  }, [setUser]);

  const login = useCallback(async (email, password) => applyAuth(await authApi.login({ email, password })), [applyAuth]);
  const signup = useCallback(async (body) => applyAuth(await authApi.signup(body)), [applyAuth]);
  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem("kt_cart");
    localStorage.removeItem("kt_my_restaurant"); localStorage.removeItem("kt_orders_cache");
  }, [setUser]);

  const value = useMemo(
    () => ({ user, loading, login, signup, logout, refresh, applyAuth, setUser, isAuthenticated: !!user }),
    [user, loading, login, signup, logout, refresh, applyAuth, setUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
